import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import Container from '@/components/generals/container'
import AppImage from '@/components/generals/app-image'
import { Eyebrow } from '@/components/generals/section-heading'
import { heroHighlights } from '@/constants/content'
import { quoteHref, sectionImages } from '@/constants/site'

const Hero: FC = (): ReactNode => {
    return (
        <section id='inicio' aria-labelledby='inicio-titulo' className='surface-grain relative flex flex-col overflow-hidden bg-black'>
            <Container className='relative z-10 flex flex-col justify-center pt-14 pb-12 sm:pt-20 lg:min-h-[calc(100svh_-_5rem)] lg:py-24'>
                <div className='max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-1000 ease-out motion-reduce:animate-none'>
                    <Eyebrow>Placas · Trofeos · Medallas · Reconocimientos</Eyebrow>

                    <h1
                        id='inicio-titulo'
                        className='mt-8 font-heading text-[2.5rem] leading-[0.98] font-medium uppercase tracking-[0.01em] text-foreground sm:text-7xl lg:text-[5.4rem] xl:text-8xl'
                    >
                        Reconocemos tus <span className='text-brass'>grandes logros.</span>
                    </h1>

                    <p className='mt-8 max-w-xl text-lg leading-relaxed text-ivory/75 sm:text-xl'>
                        Placas, trofeos y reconocimientos personalizados para celebrar aquello que merece ser recordado.
                    </p>

                    <div className='mt-10 flex flex-col gap-4 sm:flex-row'>
                        <Link href='/catalogo' className={buttonVariants({ size: 'lg' })}>
                            Explorar catálogo
                            <ArrowRight aria-hidden='true' />
                        </Link>

                        <Link href={quoteHref} className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                            Solicitar cotización
                        </Link>
                    </div>

                    <ul className='mt-14 flex flex-wrap gap-x-8 gap-y-3 border-t border-ivory/10 pt-6'>
                        {heroHighlights.map(item => (
                            <li key={item} className='flex items-center gap-3 text-[0.66rem] font-semibold uppercase tracking-[0.22em] text-ivory/70'>
                                <span aria-hidden='true' className='size-1.5 rotate-45 bg-brass' />
                                {item}
                            </li>
                        ))}
                    </ul>
                </div>
            </Container>

            <div className='relative aspect-[4/5] w-full sm:aspect-[16/11] lg:absolute lg:inset-y-0 lg:right-0 lg:aspect-auto lg:w-[66%]'>
                <AppImage
                    src={sectionImages.hero}
                    alt='Trofeo de latón cepillado, placa grabada sobre madera y reconocimiento de cristal sobre fondo negro'
                    sizes='(min-width: 1024px) 66vw, 100vw'
                    preload
                    className='object-cover object-[78%_center] lg:object-[70%_center]'
                />

                <div aria-hidden='true' className='absolute inset-0 bg-linear-to-b from-black via-black/10 to-black/70 lg:bg-linear-to-r lg:from-black lg:via-black/35 lg:to-transparent' />
                <div aria-hidden='true' className='absolute inset-x-0 bottom-0 hidden h-48 bg-linear-to-t from-black to-transparent lg:block' />
            </div>
        </section>
    )
}

export default Hero
